import { useState } from 'react';
import { Icon } from '@iconify/react';
import { FileText, Brain, Database, Shield, Users, Play, Workflow, Search } from 'lucide-react';
import IntegrationLandingBase from '@/components/integrations/IntegrationLandingBase';

export default function NotionLanding() {
  const [copiedCode, setCopiedCode] = useState(false);

  const handleCopySetupCode = async () => {
    const setupCode = `# 1. Connect Notion to Memara
Authorize Memara to access your Notion workspace

# 2. Select databases and pages
Choose which Notion content to sync with memories

# 3. Configure sync settings
Set up automatic memory creation from Notion updates

# 4. Start bidirectional sync
Changes in Notion and Memara stay synchronized`;
    try {
      await navigator.clipboard.writeText(setupCode);
      setCopiedCode(true);
      setTimeout(() => setCopiedCode(false), 2000);
    } catch (error) {
      const textArea = document.createElement('textarea');
      textArea.value = setupCode;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      setCopiedCode(true);
      setTimeout(() => setCopiedCode(false), 2000);
    }
  };

  const handleGetStarted = () => {
    window.location.href = '/login';
  };

  const handleSeeDemo = () => {
    window.open('https://www.youtube.com/watch?v=ouW739Hx7v8', '_blank');
  };

  return (
    <IntegrationLandingBase
      registryIntegrationId="notion"
      icon={
        <span className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-gray-900 shadow" aria-label="Notion Logo">
          <Icon icon="logos:notion-icon" width={48} height={48} />
        </span>
      }
      title="Notion Integration"
      description="Enterprise workspace intelligence with bidirectional memory synchronization. Transform your Notion workspace into an intelligent knowledge management system with automatic content synchronization, enhanced semantic search, and collaborative team memory capabilities."
      features={[
        { icon: <FileText className="h-4 w-4 text-gray-900" />, title: 'Intelligent Page Synchronization', description: 'Automatic bidirectional sync of Notion pages and databases with intelligent content analysis and context preservation.' },
        { icon: <Search className="h-4 w-4 text-gray-900" />, title: 'Enhanced Semantic Search', description: 'AI-powered search capabilities across all Notion content with natural language understanding and context-aware results.' },
        { icon: <Database className="h-4 w-4 text-gray-900" />, title: 'Database Memory Integration', description: 'Smart memory creation from Notion database entries with automated schema mapping and relationship understanding.' },
        { icon: <Users className="h-4 w-4 text-gray-900" />, title: 'Collaborative Workspace Memory', description: 'Shared team knowledge management with role-based access controls and collaborative intelligence features.' },
        { icon: <Workflow className="h-4 w-4 text-gray-900" />, title: 'Automated Knowledge Workflows', description: 'Intelligent automation of knowledge capture and organization with workflow-based memory creation rules.' },
        { icon: <Shield className="h-4 w-4 text-gray-900" />, title: 'Enterprise Security & Compliance', description: 'Multi-tenant workspace isolation with enterprise-grade security and comprehensive audit trail capabilities.' },
      ]}
      setupSteps={[
        'Connect Notion workspace to Memara API',
        'Select databases and pages for intelligent synchronization',
        'Configure automated sync settings and memory schemas',
        'Start bidirectional workspace memory synchronization',
      ]}
      ctas={[
        {
          label: '🚀 Get Started Free',
          onClick: handleGetStarted,
          className: 'bg-gray-900 hover:bg-gray-800 text-white px-8 py-4 h-11 rounded-md text-base font-semibold',
        },
        {
          label: 'See Demo',
          onClick: handleSeeDemo,
          className: 'border-gray-300 text-gray-900 hover:bg-gray-50 px-8 py-4 h-11 rounded-md text-base border',
          icon: <Play className="w-4 h-4 mr-2" />,
        },
        {
          label: copiedCode ? 'Copied!' : 'Copy Setup Code',
          onClick: handleCopySetupCode,
          className: 'border-gray-300 text-gray-900 hover:bg-gray-50 px-8 py-4 h-11 rounded-md text-base border',
          icon: copiedCode ? <Brain className="w-4 h-4 mr-2" /> : <Icon icon="logos:notion-icon" width={20} height={20} />,
        },
      ]}
      screenshots={[
        {
          id: 'notion-workspace-connect',
          title: 'Workspace Connection',
          description: 'Connect your Notion workspace with secure OAuth authentication and permissions',
          placeholder: 'Notion OAuth dialog showing Memara workspace connection with security permissions',
          badge: 'Connect'
        },
        {
          id: 'database-sync',
          title: 'Database Synchronization',
          description: 'Intelligent sync of Notion databases with automated schema mapping and relationships',
          placeholder: 'Notion database view showing bidirectional sync with Memara memory system',
          badge: 'Sync'
        },
        {
          id: 'enhanced-search',
          title: 'Enhanced Search',
          description: 'AI-powered semantic search across all Notion content with context understanding',
          placeholder: 'Memara search interface showing enhanced results from Notion workspace content',
          badge: 'AI Search'
        }
      ]}
      videoUrl="https://www.youtube.com/watch?v=ouW739Hx7v8"
      microcopy="Trusted by knowledge management teams, enterprise organizations, and productivity specialists using Notion for collaborative workspace management."
      trustedBy="Notion, Enterprise Knowledge Teams, and more."
      testimonial="Memara's Notion integration transforms our workspace into an intelligent knowledge system that understands context, maintains relationships, and enhances our team's collaborative intelligence capabilities."
    />
  );
} 